import React from 'react';
import Select from 'react-select';
import {FaGlobe, FaLock} from 'react-icons/fa';
import { getActionSelectStyles } from '../../utility/selectStyles';
import {getInstanceActions} from './utils';
import { useLimitedTrial } from '../../hooks/useLimitedTrial';
import {Instance} from "../../store/slices/instancesSlice";

interface InstanceRowProps {
    instance: Instance;
    handleManageDNSClick: (id: string) => void;
    handleSecurityGroupClick: (instance: Instance) => void;
    handleCreateSnapshotClick: (instance: Instance) => void;
    handleMakePublicClick: (id: string) => void;
    handleMakePrivate: (id: string, floatingIp: string) => Promise<void>;
    handleDeleteInstance: (id: string) => void;
    handleCopy: (instance: Instance, floatingIp: string) => Promise<void>;
    handleRowClick: (instance: Instance) => void;
    handleInstanceAction: (instanceId: string, action: string) => void;
    handleResizeClick: (instance: Instance) => void;
    handleUpgradeToClusterClick: (instance: Instance) => void;
    resolveImageName: (image_id: string) => string;
    resolveFlavorName: (flavor_id: string) => string;
    deletingInstances: Set<string>;
    makingPublic: any;
    makingPrivate: any;
}

const InstanceRow: React.FC<InstanceRowProps> = ({
                                                     instance,
                                                     handleManageDNSClick,
                                                     handleSecurityGroupClick,
                                                     handleCreateSnapshotClick,
                                                     handleMakePublicClick,
                                                     handleMakePrivate,
                                                     handleDeleteInstance,
                                                     handleCopy,
                                                     handleRowClick,
                                                     handleInstanceAction,
                                                     handleResizeClick,
                                                     handleUpgradeToClusterClick,
                                                     resolveImageName,
                                                     resolveFlavorName,
                                                     deletingInstances,
                                                     makingPublic,
                                                     makingPrivate
                                                 }) => {
    const isMobile = window.innerWidth <= 768;
    const isLimitedTrial = useLimitedTrial();

    const handleActionChange = (option: { value: string } | null) => {
        if (!option) return;
        const {value} = option;
        if (value === 'manage_dns') {
            handleManageDNSClick(instance.id);
        } else if (value === 'change_security_group') {
            handleSecurityGroupClick(instance);
        } else if (value === 'create_snapshot') {
            handleCreateSnapshotClick(instance);
        } else if (value === 'make_public') {
            handleMakePublicClick(instance.id);
        } else if (value === 'make_private') {
            const floatingIp = Object.values(instance.addresses || {})
                .flat()
                .find((addr: any) => addr["OS-EXT-IPS:type"] === "floating")?.addr || '';
            handleMakePrivate(instance.id, floatingIp);
        } else if (value === 'copy_ssh') {
            const floatingIp = Object.values(instance.addresses || {})
                .flat()
                .find((addr) => addr["OS-EXT-IPS:type"] === "floating")?.addr || '';
            handleCopy(instance, floatingIp);
        } else if (value === 'delete_instance') {
            handleDeleteInstance(instance.id);
        } else if (value === 'start_instance') {
            handleInstanceAction(instance.id, 'START');
        } else if (value === 'stop_instance') {
            handleInstanceAction(instance.id, 'STOP');
        } else if (value === 'soft_reboot_instance') {
            handleInstanceAction(instance.id, 'REBOOT_SOFT');
        } else if (value === 'hard_reboot_instance') {
            handleInstanceAction(instance.id, 'REBOOT_HARD');
        } else if (value === 'resize_instance') {
            handleResizeClick(instance);
        } else if (value === 'upgrade_to_cluster') {
            if (isLimitedTrial) return;
            handleUpgradeToClusterClick(instance);
        } else if (value === 'confirm_resize') {
            handleInstanceAction(instance.id, 'CONFIRM_RESIZE');
        } else if (value === 'revert_resize') {
            handleInstanceAction(instance.id, 'REVERT_RESIZE');
        }
    };

    const isPublic = instance?.metadata?.isPublic === 'true';

    // Cluster + hero badges. Built once and rendered in BOTH the table row and the mobile card:
    // a managed-datastore cluster was invisible on mobile entirely, and on desktop three Postgres
    // members read as three unrelated Ubuntu VMs with no way to tell which one held the hero role.
    const clusterRole = instance.cluster_role;                 // postgres-ha | clickhouse-ha | null
    const engineLabel = clusterRole === 'postgres-ha' ? 'PostgreSQL'
        : clusterRole === 'clickhouse-ha' ? 'ClickHouse' : null;
    const clusterTitle = instance.cluster_name
        ? (engineLabel
            ? `Managed ${engineLabel} HA cluster: ${instance.cluster_name}. Members are rebuilt automatically if one dies.`
            : `Autoscaling cluster: ${instance.cluster_name}`)
        : 'Cluster member';
    // The hero means the same thing in both models - the ONE member you reach and mutate - but the
    // route in differs, so the tooltip has to. Saying "SSH in" for a datastore member whose reach is
    // a floating IP that moves on promotion would send someone to an address that just changed.
    const heroTitle = engineLabel
        ? `Hero of "${instance.hero_of}": the single member designated as the origin for changes, so a mutation has one canonical source instead of three. If it dies, another member is promoted.`
        : `Cluster hero for "${instance.hero_of}": the member you SSH into and change. It serves traffic like any other member, so your change is live on it immediately; run a Rolling Update to carry it to the rest. If it dies, another member is promoted.`;

    // ⚠️ THE HERO BADGE MUST NOT NAME THE CLUSTER WHEN THE CLUSTER BADGE ALREADY DID.
    //    They come from different sources and disagree in wording: cluster_name is the name the user
    //    chose ("plausible"), hero_of is the Heat stack ("dbha-plausible-oqvwv5"). Printing both put
    //    TWO different names for ONE cluster on a single row - "PostgreSQL HA: plausible" beside
    //    "hero: dbha-plausible-oqvwv5" - which reads as a bug even though both values are correct.
    //    On a member row the cluster is already named, so the hero badge only has to say WHICH
    //    member. It still names the cluster on a VM that is a hero WITHOUT being a member (the
    //    pre-2026-08-07 app-cluster shape), because there is nothing else on that row to say it.
    const heroLabel = instance.cluster_member ? '★ hero' : `★ hero: ${instance.hero_of}`;

    const badges = (
        <>
            {instance.provisioning_mode === 'free' && <span className="cluster-badge" title="Included VM. Manage through MCP.">Included VM</span>}
            {instance.traffic_paused && <span className="public-badge public-badge--private" title="Public allowance reached. Private networking and disks are preserved.">Public traffic paused</span>}
            {instance.cluster_member && (
                <span className="cluster-badge" title={clusterTitle}>
                    ⬡ {engineLabel ? `${engineLabel} HA` : 'cluster'}{instance.cluster_name ? `: ${instance.cluster_name}` : ''}
                </span>
            )}
            {instance.hero_of && (
                <span className="hero-badge" title={heroTitle}>
                    {heroLabel}
                </span>
            )}
            {instance.prepared_machine && (
                <span className="prepared-badge" title={instance.prepared_machine.note ?? undefined}>
                    {instance.prepared_machine.duplicate ? 'duplicate, unused' : 'prepared for a deploy'}
                </span>
            )}
        </>
    );

    return isMobile ? (
        <>
            <div className="card-header">
                <div className="card-title-row">
                    <h3 className="card-title">{instance.name}</h3>
                    <span className={`public-badge ${isPublic ? 'public-badge--public' : 'public-badge--private'}`} title={isPublic ? 'This instance is public' : 'This instance is private'}>
                        {isPublic ? <><FaGlobe /> Public</> : <><FaLock /> Private</>}
                    </span>
                </div>
                {(instance.cluster_member || instance.hero_of || instance.prepared_machine || instance.provisioning_mode==='free' || instance.traffic_paused) && (
                    <div className="card-badge-row">{badges}</div>
                )}
                <div style={{width: '100%', marginTop: '12px'}} onClick={(e) => e.stopPropagation()}>
                    <Select
                        options={getInstanceActions(instance, deletingInstances, makingPublic, makingPrivate, isLimitedTrial)}
                        onChange={handleActionChange}
                        placeholder="Actions"
                        isSearchable={false}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={getActionSelectStyles()}
                    />
                </div>
            </div>
            <div className="card-content">

                <div className="fingerprint">
                    <span className="label">Status:</span>
                    {instance.task === 'deleting' ? (
                        <div>
                            <div className="deleting-progress"></div>
                            <span className="status-label" style={{color: '#ef4444'}}>DELETING</span>
                        </div>
                    ) : instance.status === 'BUILD' ? (
                        <div>
                            <div className="building-progress"></div>
                            <span className="status-label" style={{color: 'var(--status-info)'}}>BUILDING</span>
                        </div>
                    ) : (instance.task && instance.task !== 'N/A') ? (
                        <div>
                            <div className="building-progress"></div>
                            <span className="status-label" style={{color: 'var(--status-info)'}}>{instance.task.toUpperCase().replaceAll('_', ' ')}</span>
                        </div>
                    ) : (
                        <span className="value">{instance.status}</span>
                    )}
                </div>

                <div className="fingerprint">
                    <span className="label">Image:</span>
                    <span className="value">{resolveImageName(instance.image_id)}</span>
                </div>
                <div className="fingerprint">
                    <span className="label">Flavor:</span>
                    <span className="value">{resolveFlavorName(instance.flavor_id)}</span>
                </div>
                <div className="fingerprint">
                    <span className="label">Created:</span>
                    <span className="value">{formatCreatedDate(instance.created)}</span>
                </div>
                <div className="fingerprint">
                    <span className="label">Updated:</span>
                    <span className="value">{formatRelativeDays(instance.updated)}</span>
                </div>
                <div className="fingerprint">
                    <span className="label">Uptime:</span>
                    <span className="value">{formatUptime(instance.launched_at, instance.status)}</span>
                </div>
                <div className="fingerprint">
                    <span className="label">State:</span>
                    <span className="value">{instance.state}</span>
                </div>
                <div className="fingerprint">
                    <span className="label">Task:</span>
                    <span className="value">{instance.task ?? 'N/A'}</span>
                </div>
            </div>
        </>
    ) : (
        <tr key={instance.id} onClick={() => handleRowClick(instance)} className={`clickable-row ${instance.metadata?.tag !== 'manual' ? 'non-manual-instance' : ''} ${instance.cluster_member ? 'cluster-member-row' : ''}`}>
            <td>
                <div className="instance-name-cell">
                    <span>{instance.name}</span>
                    {badges}
                </div>
            </td>
            <td>
                {instance.task === 'deleting' ? (
                    <div>
                        <div className="deleting-progress"></div>
                        <div className="status-label" style={{color: '#ef4444'}}>DELETING</div>
                    </div>
                ) : instance.status === 'BUILD' ? (
                    <div>
                        <div className="building-progress"></div>
                        <div className="status-label" style={{color: 'var(--status-info)'}}>BUILDING</div>
                    </div>
                ) : (instance.task && instance.task !== 'N/A') ? (
                    <div>
                        <div className="building-progress"></div>
                        <div className="status-label" style={{color: 'var(--status-info)'}}>{instance.task.toUpperCase().replaceAll('_', ' ')}</div>
                    </div>
                ) : (
                    instance.status
                )}
            </td>
            <td>{instance.metadata?.imageName || resolveImageName(instance.image_id)}</td>
            <td>{instance.flavor_id ? resolveFlavorName(instance.flavor_id) : 'N/A'}</td>
            <td>{formatCreatedDate(instance.created)}</td>
            <td>
                <div className="updated-date">{formatRelativeDays(instance.updated)}</div>
            </td>
            <td>{formatUptime(instance.launched_at, instance.status)}</td>
            <td>{instance.state}</td>
            <td>{instance.task ?? 'N/A'}</td>
            <td onClick={(e) => e.stopPropagation()}>
                <Select
                    options={getInstanceActions(instance, deletingInstances, makingPublic, makingPrivate, isLimitedTrial)}
                    onChange={handleActionChange}
                    placeholder="Actions"
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={getActionSelectStyles()}
                />
            </td>
        </tr>
    );
};

// OpenStack timestamps are UTC, but Nova's launched_at omits the trailing 'Z', so
// new Date() parses it as LOCAL - throwing uptime/age off by the browser's UTC
// offset (e.g. +2h). Force UTC when no timezone designator is present.
const parseOsDate = (s: string | number | Date | null | undefined): Date =>
    (typeof s === "string" && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) ? new Date(s + "Z") : new Date(s as any);

// Exported for the free VM rows (./freeVm.ts), which show the same columns through the same formatters.
export const formatCreatedDate = (isoDate: string | number | Date) => {
    if (!isoDate) return 'Not available';
    const date = parseOsDate(isoDate);
    if (isNaN(date.getTime())) return 'Not available';
    return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).format(date);
};

export const formatRelativeDays = (isoDate: string | number | Date) => {
    if (!isoDate) return 'N/A';
    const date = parseOsDate(isoDate);
    if (isNaN(date.getTime())) return 'N/A';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 1) return 'Today';
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
};

export const formatAge = (created: string | null | undefined): string => {
    if (!created) return '-';
    const date = parseOsDate(created);
    if (isNaN(date.getTime())) return '-';
    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return '-';
    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return 'Just now';
};

export const formatUptime = (launchedAt: string | null | undefined, status: string): string => {
    if (!launchedAt) return '-';
    if (status === 'SHUTOFF' || status === 'STOPPED' || status === 'SUSPENDED') return 'Stopped';
    const date = parseOsDate(launchedAt);
    if (isNaN(date.getTime())) return '-';
    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return '-';
    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
};

export default InstanceRow;
